import { useState } from 'react';
import { useFetch } from './useFetch';
import { API_CONFIG } from '../config/api';

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
}


export const useContentUpload = () => {
  const [uploadState, setUploadState] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Função para mapear tipos de arquivo para os aceitos pela API
  const getFileType = (file: File): string => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    const typeMap: { [key: string]: string } = {
      'txt': 'txt',
      'pdf': 'pdf',
      'png': 'png',
      'jpg': 'jpeg',
      'jpeg': 'jpeg',
      'mp4': 'mp4',
      'mp3': 'mp3',
      'docx': 'docx',
      'xlsx': 'xlsx',
      'pptx': 'pptx',
    };
    
    return typeMap[extension || ''] || 'txt'; // Default para txt se não encontrar
  };

  const uploadContent = async (file: File, name?: string, description?: string): Promise<string | null> => {
    setUploadState({
      isUploading: true,
      progress: 0,
      error: null,
      success: false,
    });

    try {
      // 1. Criar conteúdo na API
      setUploadState(prev => ({ ...prev, progress: 15 }));

      const fileType = getFileType(file);

      // Usar nome personalizado se fornecido, senão usar nome do arquivo
      const finalName = name?.trim() || file.name;
      const finalDescription = description?.trim() || `Arquivo: ${file.name}`;

      const createResponse = await fetchWithAuth(`${API_CONFIG.BASE_URL}/contents`, {
        method: 'POST',
        body: JSON.stringify({
          name: finalName,
          description: finalDescription,
          type: fileType,
        }),
      });

      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        throw new Error(`Erro ao criar conteúdo: ${createResponse.status} - ${errorText}`);
      }

      const { id: contentId, url: presignedUrl } = await createResponse.json();

      // 2. Upload para S3
      setUploadState(prev => ({ ...prev, progress: 45 }));

      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        const errorBody = await uploadResponse.text();
        throw new Error(`Erro no upload para S3: ${uploadResponse.status} - ${errorBody}`);
      }
      
      // Animação suave para 100%
      setUploadState(prev => ({ ...prev, progress: 80 }));
      
      // Aguardar um pouco antes de mostrar 100%
      setTimeout(() => {
        setUploadState(prev => ({ ...prev, progress: 100, success: true }));
      }, 300);

      return contentId;
    } catch (error) {
      setUploadState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isUploading: false,
      }));
      return null;
    }
  };

  const resetUploadState = () => {
    setUploadState({
      isUploading: false,
      progress: 0,
      error: null,
      success: false,
    });
  };

  return {
    uploadContent,
    uploadState,
    resetUploadState,
  };
};
